import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useFilters, periodToDates } from "../lib/filters";
import { PageHeader } from "../components/PageHeader";
import { PageSkeleton } from "../components/LoadingSkeleton";
import { EmptyState } from "../components/EmptyState";
import {
  IconLightbulb, IconRefresh, IconAlertTriangle, IconCheck, IconInfo, IconArrowUp,
  IconChevronRight, IconCopy, IconUser, IconClock,
} from "../components/icons";
import type { IntelligenceDto, IntelItem, IntelAction, IntelSeverity, IntelTheme, IntelDiagnosis } from "@bi/types";

type ThemeFilter = "all" | IntelTheme;

const THEMES: Array<{ value: ThemeFilter; label: string }> = [
  { value: "all",     label: "Tudo" },
  { value: "caixa",   label: "Caixa" },
  { value: "receita", label: "Receita" },
  { value: "margem",  label: "Margem" },
  { value: "frota",   label: "Frota" },
  { value: "dados",   label: "Qualidade dos dados" },
];

const SEVERITY_META: Record<IntelSeverity, { label: string; chip: string; bar: string; dot: string; icon: React.ComponentType<{ className?: string }> }> = {
  critical:    { label: "Crítico",      chip: "bg-danger-50 text-danger-700 border-danger-100",    bar: "bg-danger-500",  dot: "bg-danger-500",  icon: IconAlertTriangle },
  warning:     { label: "Atenção",      chip: "bg-warning-50 text-warning-700 border-warning-100", bar: "bg-warning-500", dot: "bg-warning-500", icon: IconAlertTriangle },
  opportunity: { label: "Oportunidade", chip: "bg-tm-50 text-tm-700 border-tm-200",                bar: "bg-tm-500",      dot: "bg-tm-500",      icon: IconArrowUp },
  info:        { label: "Informativo",  chip: "bg-info-50 text-info-700 border-info-100",          bar: "bg-info-500",    dot: "bg-info-500",    icon: IconInfo },
};

const WHEN_META: Record<NonNullable<IntelAction["when"]>, { label: string; cls: string }> = {
  "hoje":        { label: "Hoje",        cls: "bg-danger-50 text-danger-700" },
  "esta semana": { label: "Esta semana", cls: "bg-warning-50 text-warning-700" },
  "este mês":    { label: "Este mês",    cls: "bg-surface-muted text-gray-600" },
};

/** Fallback de tema para itens sem `theme` (respostas antigas da API). */
const AREA_THEME: Record<IntelItem["area"], IntelTheme> = {
  financeiro: "caixa", fluxo_caixa: "caixa", operacional: "receita", dre: "margem", frota: "frota",
};
const themeOf = (i: IntelItem): IntelTheme => i.theme ?? AREA_THEME[i.area];

const brlCompact = (v: number) => {
  const a = Math.abs(v);
  if (a >= 1_000_000) return `R$ ${(a / 1_000_000).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} mi`;
  if (a >= 10_000) return `R$ ${Math.round(a / 1000).toLocaleString("pt-BR")} mil`;
  return a.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

/** Ações do item: passos da API ou, em respostas antigas, a recomendação. */
const actionsOf = (it: IntelItem): IntelAction[] =>
  it.actions?.length ? it.actions : it.recommendation ? [{ text: it.recommendation }] : [];

/** Texto do plano de ação, para colar no WhatsApp/e-mail. */
function planText(items: IntelItem[], periodLabel: string): string {
  const lines = [`*Plano de ação · ${periodLabel}*`, ""];
  items.forEach((it, i) => {
    lines.push(`*${i + 1}. ${it.title}*${it.impactLabel ? ` (${it.impactLabel})` : ""}`);
    actionsOf(it).forEach((a) => {
      const meta = [a.owner, a.when].filter(Boolean).join(", ");
      lines.push(`  • ${a.title ?? a.text}${meta ? ` [${meta}]` : ""}`);
      if (a.title && a.detail) lines.push(`    ${a.detail}`);
      if (a.expected) lines.push(`    Resultado esperado: ${a.expected}`);
      (a.targets ?? []).forEach((t) => lines.push(`    - ${t.label}${t.value ? `: ${t.value}` : ""}${t.note ? ` (${t.note})` : ""}`));
      const hidden = (a.targetsTotal ?? 0) - (a.targets ?? []).length;
      if (hidden > 0) lines.push(`    - e mais ${hidden}`);
    });
    lines.push("");
  });
  return lines.join("\n").trim();
}

export function IntelligencePage() {
  const qc = useQueryClient();
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);

  const [theme, setTheme] = useState<ThemeFilter>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["intelligence", from, to, filters.comparison],
    queryFn: () =>
      api.get<IntelligenceDto>(
        `/dashboards/transport/intelligence?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
      ),
    staleTime: 60_000,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["intelligence"] });

  const { priorities, monitored } = useMemo(() => {
    const all = (data?.items ?? []).filter((i) => theme === "all" || themeOf(i) === theme);
    return {
      priorities: all.filter((i) => i.severity !== "info"),
      monitored: all.filter((i) => i.severity === "info"),
    };
  }, [data, theme]);

  // Seleção acompanha o filtro: se a prioridade aberta saiu da lista, abre a primeira.
  useEffect(() => {
    if (!priorities.some((p) => p.id === selectedId)) setSelectedId(priorities[0]?.id ?? null);
  }, [priorities, selectedId]);

  const copy = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 2000);
    } catch { /* clipboard bloqueado: sem feedback */ }
  };

  if (isLoading) return <PageSkeleton />;

  if (error || !data) {
    return (
      <div className="space-y-6">
        <PageHeader title="Insights e Alertas" subtitle="O que mudou no período, por quê e o que fazer" />
        <EmptyState
          icon={<IconLightbulb className="w-7 h-7" />}
          title="Não foi possível carregar a inteligência"
          description={(error as Error)?.message ?? "Tente novamente em instantes."}
        />
      </div>
    );
  }

  const periodLabel = data.items[0]?.periodLabel ?? data.period.label;
  const countBy = (t: ThemeFilter) =>
    data.items.filter((i) => i.severity !== "info" && (t === "all" || themeOf(i) === t)).length;
  const selected = priorities.find((p) => p.id === selectedId) ?? null;
  const selectedRank = selected ? priorities.indexOf(selected) + 1 : 0;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Insights e Alertas"
        subtitle="O que mudou no período, por quê e o que fazer, calculado com os dados reais"
        actions={
          <div className="flex gap-2">
            {priorities.length > 0 && (
              <button onClick={() => copy("plano", planText(priorities, periodLabel))} className="btn-secondary">
                {copied === "plano" ? <IconCheck className="w-4 h-4" /> : <IconCopy className="w-4 h-4" />}
                {copied === "plano" ? "Plano copiado" : "Copiar plano completo"}
              </button>
            )}
            <button onClick={refresh} className="btn-secondary" disabled={isFetching}>
              <IconRefresh className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
              Atualizar
            </button>
          </div>
        }
      />

      {data.diagnosis && (
        <DiagnosisHero diagnosis={data.diagnosis} periodLabel={periodLabel}
          active={theme} onTheme={(t) => setTheme((cur) => (cur === t ? "all" : t))} />
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {THEMES.map((t) => {
          const n = countBy(t.value);
          if (t.value !== "all" && !data.items.some((i) => themeOf(i) === t.value)) return null;
          return (
            <button key={t.value} onClick={() => setTheme(t.value)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                theme === t.value ? "bg-tm-600 text-white" : "bg-white border border-surface-border text-gray-600 hover:bg-surface-muted"
              }`}>
              {t.label}
              {n > 0 && <span className={`tabular-nums ${theme === t.value ? "text-tm-100" : "text-gray-400"}`}>{n}</span>}
            </button>
          );
        })}
      </div>

      {priorities.length === 0 ? (
        <div className="flex items-center gap-3 rounded-xl border border-tm-200 bg-tm-50 px-5 py-4">
          <span className="w-9 h-9 rounded-full bg-tm-100 grid place-items-center flex-shrink-0">
            <IconCheck className="w-5 h-5 text-tm-600" />
          </span>
          <p className="text-sm font-semibold text-tm-800">Nada pedindo ação {theme === "all" ? "no período" : "neste tema"}.</p>
        </div>
      ) : (
        /* Central de comando: lista de prioridades à esquerda, a selecionada em
           detalhe à direita. Antes, cada prioridade abria um bloco enorme na
           própria lista e a página ficava muito longa. */
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(300px,360px)_1fr] gap-4 items-start">
          <nav aria-label="Prioridades de ação" className="card overflow-hidden lg:sticky lg:top-20">
            <div className="px-4 py-3 border-b border-surface-border">
              <p className="text-sm font-semibold text-gray-900">Prioridades de ação</p>
              <p className="text-[11px] text-gray-400 mt-0.5">Pela gravidade e pelo valor em jogo</p>
            </div>
            <ul className="divide-y divide-surface-border lg:max-h-[calc(100vh-12rem)] lg:overflow-y-auto">
              {priorities.map((it, i) => {
                const sel = it.id === selectedId;
                const meta = SEVERITY_META[it.severity];
                return (
                  <li key={it.id}>
                    <button onClick={() => setSelectedId(it.id)} aria-current={sel}
                      className={`w-full text-left px-4 py-3 flex gap-3 transition-colors border-l-[3px] ${
                        sel ? `bg-tm-50/70 ${it.severity === "critical" ? "border-l-danger-500" : it.severity === "warning" ? "border-l-warning-500" : "border-l-tm-500"}` : "border-l-transparent hover:bg-surface-muted/60"}`}>
                      <span className={`text-sm font-semibold tabular-nums w-4 text-right ${sel ? "text-gray-700" : "text-gray-300"}`}>{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} aria-hidden="true" />
                          <span className="text-[11px] text-gray-400 truncate">{it.category}</span>
                        </div>
                        <p className={`mt-0.5 text-sm leading-snug ${sel ? "font-semibold text-gray-900" : "font-medium text-gray-800"}`}>{it.title}</p>
                        {it.impactLabel && <p className="mt-0.5 text-xs text-gray-500 truncate">{it.impactLabel}</p>}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {selected && (
            <PriorityDetail item={selected} rank={selectedRank}
              copied={copied === selected.id}
              onCopy={() => copy(selected.id, planText([selected], periodLabel))} />
          )}
        </div>
      )}

      {monitored.length > 0 && (
        <details className="group">
          <summary className="card px-5 py-4 flex items-center gap-2 cursor-pointer select-none hover:bg-surface-muted/60 [&::-webkit-details-marker]:hidden marker:hidden">
            <IconChevronRight className="w-3.5 h-3.5 text-gray-400 transition-transform group-open:rotate-90" />
            <span className="text-sm font-semibold text-gray-900">Monitorados, sem ação necessária</span>
            <span className="text-xs text-gray-400">· {monitored.length} {monitored.length === 1 ? "indicador" : "indicadores"}</span>
          </summary>
          <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
            {monitored.map((it) => (
              <div key={it.id} className="card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs text-gray-400">{it.category}</p>
                    <p className="text-sm font-semibold text-gray-900">{it.title}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-gray-900 whitespace-nowrap">{it.mainValue}</p>
                    {it.comparison && <p className="text-[11px] text-gray-400">{it.comparison}</p>}
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-600 leading-relaxed">{it.description}</p>
                {it.recommendation && <p className="mt-2 text-xs text-gray-500">{it.recommendation}</p>}
              </div>
            ))}
          </div>
        </details>
      )}

      {data.warnings.length > 0 && (
        <div className="text-xs text-gray-500 space-y-1">
          {data.warnings.map((w, i) => (
            <p key={i} className="flex items-start gap-1.5"><IconInfo className="w-3.5 h-3.5 flex-shrink-0 mt-px text-gray-400" />{w}</p>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Leitura do período ──────────────────────────────────────────────────────

function DiagnosisHero({ diagnosis, periodLabel, active, onTheme }: {
  diagnosis: IntelDiagnosis; periodLabel: string; active: ThemeFilter; onTheme: (t: IntelTheme) => void;
}) {
  const tone = {
    critical: { dot: "bg-danger-400", label: "Requer ação" },
    warning:  { dot: "bg-warning-400", label: "Pede atenção" },
    ok:       { dot: "bg-tm-300", label: "Sob controle" },
  }[diagnosis.tone];
  const dados = diagnosis.themes.find((t) => t.key === "dados");
  return (
    <section aria-labelledby="leitura-periodo" className="rounded-2xl bg-tm-800 text-white overflow-hidden">
      <div className="px-6 py-5">
        <div className="flex flex-wrap items-center gap-2 text-xs text-tm-100">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 font-medium">
            <span className={`w-2 h-2 rounded-full ${tone.dot}`} aria-hidden="true" />
            {tone.label}
          </span>
          <span>Leitura do período · {periodLabel}</span>
        </div>
        <h2 id="leitura-periodo" className="mt-3 max-w-4xl text-lg leading-snug text-white font-medium">
          {diagnosis.headline}
        </h2>

        {diagnosis.themes.length > 0 && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            {diagnosis.themes.filter((t) => t.key !== "dados").slice(0, 4).map((t) => {
              const sel = active === t.key;
              return (
                <button key={t.key} onClick={() => onTheme(t.key)}
                  className={`text-left rounded-xl px-4 py-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${
                    sel ? "bg-white text-gray-900" : "bg-white/[0.07] hover:bg-white/[0.12]"}`}>
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${SEVERITY_META[t.severity].dot}`} aria-hidden="true" />
                    <span className={`text-xs font-semibold uppercase tracking-wide ${sel ? "text-gray-500" : "text-tm-100"}`}>{t.title}</span>
                    {t.impact != null && (
                      <span className={`ml-auto text-xs font-semibold tabular-nums ${sel ? "text-gray-900" : "text-white"}`}>{brlCompact(t.impact)}</span>
                    )}
                  </div>
                  <p className={`mt-1.5 text-[13px] leading-snug ${sel ? "text-gray-700" : "text-tm-50/90"}`}>{t.summary}</p>
                </button>
              );
            })}
          </div>
        )}
        {dados && (
          <button onClick={() => onTheme("dados")} className="mt-3 inline-flex items-center gap-1.5 text-xs text-tm-100 hover:text-white text-left">
            <IconInfo className="w-3.5 h-3.5 flex-shrink-0" />
            {dados.summary}
          </button>
        )}
      </div>
    </section>
  );
}

// ── Prioridade selecionada ──────────────────────────────────────────────────

function PriorityDetail({ item, rank, onCopy, copied }: {
  item: IntelItem; rank: number; onCopy: () => void; copied: boolean;
}) {
  const meta = SEVERITY_META[item.severity];
  const drivers = item.drivers ?? [];
  const actions = actionsOf(item);

  return (
    <article className="card overflow-hidden" aria-labelledby={`prio-${item.id}`}>
      {/* O quê + quanto */}
      <header className="px-6 pt-5 pb-4 border-b border-surface-border">
        <div className="flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-gray-400">Prioridade {rank}</span>
              <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${meta.chip}`}>
                <meta.icon className="w-3 h-3" />{meta.label}
              </span>
              <span className="text-xs text-gray-400">{item.category}</span>
            </div>
            <h3 id={`prio-${item.id}`} className="mt-2 text-xl font-semibold text-gray-900 leading-snug">{item.title}</h3>
            <p className="mt-1.5 text-sm text-gray-600 leading-relaxed max-w-3xl">{item.description}</p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="font-display text-2xl font-semibold text-gray-900 whitespace-nowrap tabular-nums">{item.mainValue}</p>
            {item.comparison && <p className="text-xs text-gray-400 mt-0.5">{item.comparison}</p>}
            {item.impactLabel && (
              <p className={`mt-2 inline-block rounded-md px-2 py-1 text-xs font-semibold ${item.severity === "critical" ? "bg-danger-50 text-danger-700" : "bg-surface-muted text-gray-700"}`}>
                {item.impactLabel}
              </p>
            )}
          </div>
        </div>
      </header>

      <div className={`grid grid-cols-1 ${drivers.length > 0 ? "xl:grid-cols-[1fr_minmax(260px,34%)]" : ""}`}>
        {/* Plano de ação: o centro da tela */}
        <section className="px-6 py-5" aria-label="Plano de ação">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-sm font-semibold text-gray-900">Plano de ação</h4>
            {actions.length > 0 && (
              <button type="button" onClick={onCopy} className="inline-flex items-center gap-1 text-xs font-medium text-tm-700 hover:text-tm-900">
                {copied ? <IconCheck className="w-3.5 h-3.5" /> : <IconCopy className="w-3.5 h-3.5" />}
                {copied ? "Copiado" : "Copiar este plano"}
              </button>
            )}
          </div>
          {actions.length === 0 ? (
            <p className="mt-3 text-sm text-gray-500">Sem ação sugerida para este ponto.</p>
          ) : (
            <ol className="mt-3 space-y-3">
              {actions.map((a, i) => <ActionStep key={i} action={a} n={i + 1} last={i === actions.length - 1} />)}
            </ol>
          )}
        </section>

        {/* Por quê: as evidências */}
        {drivers.length > 0 && (
          <aside className="px-6 py-5 border-t xl:border-t-0 xl:border-l border-surface-border bg-surface-muted/40" aria-label="Por quê">
            <h4 className="text-sm font-semibold text-gray-900">Por quê</h4>
            <p className="text-xs text-gray-500 mt-0.5">{item.driversTitle ?? "O que explica o número"}</p>
            <ul className="mt-3 space-y-3">
              {drivers.map((d, i) => {
                const body = (
                  <>
                    <div className="flex items-baseline gap-2">
                      <span className={`text-[13px] truncate ${d.href ? "text-tm-700 group-hover/drv:underline" : "text-gray-800"}`} title={d.label}>{d.label}</span>
                      <span className={`ml-auto text-[13px] font-semibold whitespace-nowrap tabular-nums ${d.tone === "bad" ? "text-danger-600" : d.tone === "good" ? "text-tm-700" : "text-gray-900"}`}>{d.value}</span>
                    </div>
                    {d.share != null && (
                      <div className="mt-1 h-1.5 rounded-full bg-white overflow-hidden">
                        <div className={`h-full rounded-full ${d.tone === "bad" ? "bg-danger-400" : d.tone === "good" ? "bg-tm-400" : "bg-gray-400"}`}
                          style={{ width: `${Math.max(2, Math.min(100, d.share * 100))}%` }} />
                      </div>
                    )}
                    {d.detail && <p className="mt-0.5 text-[11px] text-gray-500 truncate" title={d.detail}>{d.detail}</p>}
                  </>
                );
                return <li key={i}>{d.href ? <Link to={d.href} className="block group/drv">{body}</Link> : body}</li>;
              })}
            </ul>
          </aside>
        )}
      </div>

      <footer className="px-6 py-3 border-t border-surface-border flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400">
        {(item.links ?? []).map((l) => (
          <Link key={l.href} to={l.href} className="inline-flex items-center gap-0.5 font-medium text-tm-700 hover:underline">
            Ir para {l.label} <IconChevronRight className="w-3 h-3" />
          </Link>
        ))}
        <span>Período: {item.periodLabel}</span>
        {item.source && <span className="truncate">Fonte: {item.source}</span>}
      </footer>
    </article>
  );
}

/** Um passo do plano: título, quem/quando, como, resultado esperado e alvos. */
function ActionStep({ action: a, n, last }: { action: IntelAction; n: number; last: boolean }) {
  const when = a.when ? WHEN_META[a.when] : null;
  const targets = (a.targets ?? []).filter((t) => t.label);
  const hiddenTargets = Math.max(0, (a.targetsTotal ?? 0) - targets.length);
  return (
    <li className="relative flex gap-3">
      {/* Linha do passo a passo */}
      {!last && <span className="absolute left-[13px] top-8 bottom-[-12px] w-px bg-tm-100" aria-hidden="true" />}
      <span className="relative w-7 h-7 rounded-full bg-tm-600 text-white text-xs font-semibold grid place-items-center flex-shrink-0">{n}</span>
      <div className="flex-1 min-w-0 rounded-xl border border-surface-border bg-white p-4">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <p className="text-sm font-semibold text-gray-900 mr-auto">{a.title ?? a.text}</p>
          {a.owner && (
            <span className="inline-flex items-center gap-1 rounded-full bg-info-50 px-2 py-0.5 text-[11px] font-medium text-info-700">
              <IconUser className="w-3 h-3" />{a.owner}
            </span>
          )}
          {when && (
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${when.cls}`}>
              <IconClock className="w-3 h-3" />{when.label}
            </span>
          )}
        </div>
        {a.title && a.detail && <p className="mt-1.5 text-[13px] text-gray-600 leading-relaxed">{a.detail}</p>}
        {a.expected && (
          <p className="mt-2 inline-flex items-start gap-1.5 text-[13px] text-tm-800">
            <IconArrowUp className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-tm-600" />
            <span><span className="font-semibold">Resultado esperado:</span> {a.expected}</span>
          </p>
        )}
        {targets.length > 0 && (
          <div className="mt-3 rounded-lg border border-surface-border overflow-hidden">
            <table className="w-full text-[13px]">
              <tbody className="divide-y divide-surface-border">
                {targets.map((t, i) => (
                  <tr key={i} className="hover:bg-surface-muted/50">
                    <td className="px-3 py-1.5 min-w-0">
                      {t.href
                        ? <Link to={t.href} className="font-medium text-tm-700 hover:underline">{t.label}</Link>
                        : <span className="font-medium text-gray-800">{t.label}</span>}
                      {t.note && <span className="ml-2 text-[11px] text-gray-400">{t.note}</span>}
                    </td>
                    {t.value && <td className="px-3 py-1.5 text-right font-semibold text-gray-900 whitespace-nowrap tabular-nums">{t.value}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
            {hiddenTargets > 0 && (
              <p className="border-t border-surface-border bg-surface-muted/40 px-3 py-1.5 text-[12px] text-gray-500">
                Mostrando os {targets.length} mais urgentes de {a.targetsTotal}.
                {a.href && <> <Link to={a.href} className="font-medium text-tm-700 hover:underline">Ver todos ({a.targetsTotal})</Link></>}
              </p>
            )}
          </div>
        )}
        {a.href && (
          <Link to={a.href} className="mt-3 inline-flex items-center gap-1 rounded-lg border border-tm-200 bg-tm-50 px-3 py-1.5 text-xs font-medium text-tm-700 hover:bg-tm-100">
            {a.hrefLabel ?? "Abrir"} <IconChevronRight className="w-3 h-3" />
          </Link>
        )}
      </div>
    </li>
  );
}
